import React from 'react';

const wrapStyle = {
  maxWidth: '800px',
  margin: '0 auto',
  padding: '48px 32px 96px',
  fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
  color: '#0F1923',
  lineHeight: 1.65,
};

const h1Style = {
  fontFamily: '"Fraunces", Georgia, serif',
  fontSize: '36px',
  fontWeight: 600,
  marginBottom: '8px',
  color: '#0F1923',
};

const h2Style = {
  fontFamily: '"Fraunces", Georgia, serif',
  fontSize: '22px',
  fontWeight: 600,
  marginTop: '32px',
  marginBottom: '8px',
  color: '#0F1923',
};

const metaStyle = {
  fontSize: '14px',
  color: '#6B7280',
  marginBottom: '32px',
};

const pStyle = { fontSize: '15px', marginBottom: '12px' };

export function TermsPage() {
  return (
    <div style={wrapStyle} data-testid="terms-page">
      <h1 style={h1Style}>End User License Agreement</h1>
      <div style={metaStyle}>Effective Date: February 1, 2026</div>

      <p style={pStyle}>
        This End User License Agreement ("Agreement") is between Steele Martin Jones &amp; Company, PLC
        ("Firm," "we," "us") and the user ("you") accessing the SMJ Client Financial Portal
        (the "Service"). By accessing or using the Service, you agree to the terms below.
      </p>

      <h2 style={h2Style}>1. License</h2>
      <p style={pStyle}>
        We grant you a limited, non-exclusive, non-transferable, revocable license to access and
        use the Service solely for your internal business or personal financial reporting purposes
        in connection with services provided by the Firm.
      </p>

      <h2 style={h2Style}>2. Permitted Use</h2>
      <p style={pStyle}>
        You may use the Service to view financial statements, key performance indicators, and
        related reports prepared by the Firm. You may not resell, sublicense, or commercially
        redistribute any portion of the Service or its output without prior written consent.
      </p>

      <h2 style={h2Style}>3. Data &amp; QuickBooks Online</h2>
      <p style={pStyle}>
        The Service may, with your authorization, connect to your QuickBooks Online ("QBO") company
        to retrieve accounting data. We use Intuit's official OAuth 2.0 authorization flow and
        store only the tokens necessary to maintain the connection. You may revoke access at any
        time through the Service or your Intuit account.
      </p>

      <h2 style={h2Style}>4. Confidentiality</h2>
      <p style={pStyle}>
        The Firm treats client financial information as confidential and applies reasonable
        administrative, technical, and physical safeguards. You agree not to share your login
        credentials and to notify the Firm immediately of any suspected unauthorized access.
      </p>

      <h2 style={h2Style}>5. Disclaimer of Warranty</h2>
      <p style={pStyle}>
        The Service is provided "as is" without warranties of any kind. Output of the Service is
        based on data you or your authorized representatives provide; the Firm does not guarantee
        completeness or absence of error in source data imported from third-party systems.
      </p>

      <h2 style={h2Style}>6. Limitation of Liability</h2>
      <p style={pStyle}>
        To the maximum extent permitted by law, the Firm's aggregate liability for any claim
        arising out of or relating to the Service is limited to the fees paid to the Firm for the
        engagement to which the Service relates.
      </p>

      <h2 style={h2Style}>7. Termination</h2>
      <p style={pStyle}>
        We may suspend or terminate your access to the Service at any time for breach of this
        Agreement or upon termination of the underlying engagement. You may stop using the Service
        at any time.
      </p>

      <h2 style={h2Style}>8. Contact</h2>
      <p style={pStyle}>
        Questions about this Agreement may be directed to Steele Martin Jones &amp; Company, PLC,
        Jackson, Tennessee.
      </p>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <div style={wrapStyle} data-testid="privacy-page">
      <h1 style={h1Style}>Privacy Policy</h1>
      <div style={metaStyle}>Effective Date: February 1, 2026</div>

      <p style={pStyle}>
        Steele Martin Jones &amp; Company, PLC ("Firm," "we") operates the SMJ Client Financial
        Portal (the "Service"). This Privacy Policy explains what information we collect, how we
        use it, and the choices you have.
      </p>

      <h2 style={h2Style}>1. Information We Collect</h2>
      <p style={pStyle}>
        <strong>Account Information:</strong> name, email address, role (staff or client), and
        authentication credentials.
      </p>
      <p style={pStyle}>
        <strong>Client Financial Data:</strong> trial balance and chart of accounts data you
        upload or authorize us to import from QuickBooks Online or QuickBooks Desktop.
      </p>
      <p style={pStyle}>
        <strong>QuickBooks Online Tokens:</strong> if you authorize a QBO connection, we store the
        OAuth access and refresh tokens and the Intuit company (realm) identifier so the Service
        can retrieve data on your behalf.
      </p>
      <p style={pStyle}>
        <strong>Usage Data:</strong> standard server logs (timestamps, IP, request paths) used to
        operate and secure the Service.
      </p>

      <h2 style={h2Style}>2. How We Use Information</h2>
      <p style={pStyle}>
        We use the information to: (a) authenticate you and authorize access, (b) provide financial
        reporting and dashboards, (c) retrieve and process data you authorize from third-party
        accounting platforms, (d) provide professional services under our engagement, and (e)
        safeguard the Service.
      </p>

      <h2 style={h2Style}>3. Sharing</h2>
      <p style={pStyle}>
        We do not sell personal information. We share information only with: (a) authorized
        employees and contractors of the Firm bound by confidentiality, (b) infrastructure
        providers acting on our instructions, and (c) when required by law or to protect rights
        and safety.
      </p>

      <h2 style={h2Style}>4. QuickBooks Online Data Handling</h2>
      <p style={pStyle}>
        Data imported from QuickBooks Online is used solely to render financial statements,
        analyses, and reports inside the Service for the authorized client. We do not use QBO data
        for advertising or share it with third parties outside the scope of the engagement.
      </p>

      <h2 style={h2Style}>5. Data Retention</h2>
      <p style={pStyle}>
        We retain client data for the duration of the engagement and for any period required by
        applicable professional standards and law. You may request disconnection of your QBO
        company at any time, which revokes our access tokens.
      </p>

      <h2 style={h2Style}>6. Security</h2>
      <p style={pStyle}>
        We use industry-standard administrative, technical, and physical safeguards including
        encryption in transit, access controls, and role-based authorization. No method of
        transmission or storage is 100% secure.
      </p>

      <h2 style={h2Style}>7. Your Choices</h2>
      <p style={pStyle}>
        You may update your account information, revoke QBO access, or request deletion of your
        account by contacting the Firm. Some information may be retained as required to comply
        with legal or professional obligations.
      </p>

      <h2 style={h2Style}>8. Changes</h2>
      <p style={pStyle}>
        We may update this Privacy Policy from time to time. Material changes will be communicated
        through the Service or by email.
      </p>

      <h2 style={h2Style}>9. Contact</h2>
      <p style={pStyle}>
        For privacy questions, contact Steele Martin Jones &amp; Company, PLC, Jackson, Tennessee.
      </p>
    </div>
  );
}
